import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, switchMap, filter, tap} from 'rxjs/operators';
import {ChangeImageDialogData} from '@q9elements/ui-kit/common';

import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';
import {StylesService} from '../styles/styles.service';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {MxStyleObject} from '../styles/styles.model';
import {FL_ENDING_TYPE} from './header.model';
import {EditorRegistryService} from '../mx-graph/editor/editor-registry.service';
import {SFA_HEADER_HEIGHT} from 'src/app/editor-interface/toolbar/util/constants';

export enum LegendItemType {
  Line = 'line',
  Card = 'card',
  Resource = 'resource'
}

export interface IFLStyle {
  borderColor: string;
  borderStyle: string;
  borderWidth: string;
  endArrow: FL_ENDING_TYPE;
  startArrow: FL_ENDING_TYPE;
}

export interface ILegendItem {
  mxKey: number;
  text: string;
  itemType: LegendItemType;
  image?: string;
  style?: IFLStyle;
  dynamicStyle?: {end: any; start: any};
  description?: string;
}

export interface IHeaderData {
  legendItems: ILegendItem[];
  title?: string;
  description?: string;
  logo?: string;
  color?: string;
}

@Injectable()
export class SfaHeaderService {
  public readonly COLOR_MODAL_ID = 'header-color';

  public headerData$: Observable<IHeaderData> = this.diagramJsonSvc.diagramJson$.pipe(
    map(json => this.collectHeaderData(json))
  );
  public pinned = true;

  private headerColorPreviewSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public previewColor$: Observable<string> = this.headerColorPreviewSubject.asObservable();

  constructor(
    private diagramJsonSvc: DiagramJsonHandlerService,
    private diagramUpdateSvc: DiagramUpdateService,
    private stylesSvc: StylesService,
    private popupSvc: PopupManagerService,
    private editorRegistryService: EditorRegistryService
  ) {}

  get isPinned() {
    return this.pinned;
  }

  togglePin() {
    this.pinned = !this.pinned;
    const graph = this.editorRegistryService.getGraph();

    graph.adjustForVisibleToolbar(this.pinned ? SFA_HEADER_HEIGHT : -SFA_HEADER_HEIGHT);
  }

  async createLegendItemFromCell(cell) {
    // only activity or flowline is expected
    const json = cell.getJson();
    const itemType = cell.edge ? LegendItemType.Line : LegendItemType.Card;
    const payload = await this.popupSvc.openLegendItemTextDialog().toPromise();

    if (!payload?.text) {
      return;
    }

    const style = this.getStyle(itemType, json);
    const mxKey = this.getMxKey();
    const action = {
      createLegendItem: {
        itemType,
        style,
        mxKey,
        ...payload
      }
    };

    await this.diagramUpdateSvc.update(action);
  }

  async createResourceLegendItem(resource) {
    const itemType = LegendItemType.Resource;
    const text = resource.name;
    const mxKey = this.getMxKey();
    const dbId = resource.resourceDbId || undefined;
    const image = resource.icon || undefined;
    const action = {
      createLegendItem: {
        itemType,
        text,
        mxKey,
        dbId,
        image
      }
    };

    await this.diagramUpdateSvc.update(action);
  }

  async editLegendItem(item: ILegendItem) {
    const payload = await this.popupSvc
      .openLegendItemTextDialog({text: item.text, description: item.description, isEdit: true})
      .toPromise();

    if (payload) {
      const action = {editLegendItem: {mxKey: item.mxKey, ...payload}};

      await this.diagramUpdateSvc.update(action);
    }
  }

  public changeLogo({event, isUpload, callback: cb}): Observable<any> {
    let blob;
    const changeLogoData = this.getChangeLogoData(event, isUpload);

    return this.popupSvc.openChangeLogoDialog(changeLogoData).pipe(
      tap(() => cb()),
      filter(({key}) => !!key),
      tap(({img}) => (blob = img)),
      switchMap(({key}) => {
        const action = {changeHeaderLogo: {key}};

        return this.diagramUpdateSvc.update(action);
      }),
      map(() => blob)
    );
  }

  async removeLogo() {
    const action = {removeLogo: {remove: true}};

    await this.diagramUpdateSvc.update(action);
  }

  alreadyAdded(resource) {
    const legendItems = this.diagramJsonSvc.get()?.headerData?.legendItems || [];

    return legendItems.some(
      item => item.itemType === LegendItemType.Resource && item.text === resource.name
    );
  }

  async deleteLegendItem(mxKey: number) {
    const action = {
      deleteLegendItem: {
        mxKey
      }
    };

    await this.diagramUpdateSvc.update(action);
  }

  async reorderLegendItem(mxKey, position) {
    const action = {
      reorderLegendItem: {
        mxKey: Number(mxKey),
        position
      }
    };

    await this.diagramUpdateSvc.update(action);
  }

  async changeColor(color: string) {
    const newColor = await this.popupSvc
      .openHeaderColorDialog(color, this.COLOR_MODAL_ID)
      .toPromise();

    if (color === newColor || !newColor) {
      return;
    }

    const action = {changeColor: {color: newColor}};

    await this.diagramUpdateSvc.update(action);
  }

  public nextPreviewColor(color: string): void {
    this.headerColorPreviewSubject.next(color);
  }

  public colorModalOpen(): boolean {
    return this.popupSvc.isDialogOpen(this.COLOR_MODAL_ID);
  }

  private getStyle(itemType: LegendItemType, json?) {
    if (itemType === LegendItemType.Card) {
      const {
        borderColor,
        backgroundColor,
        borderWidth,
        borderStyle = 'solid'
      } = this.stylesSvc.activity(json).boundingBox();

      return {borderColor, backgroundColor, borderWidth, borderStyle};
    }

    if (itemType === LegendItemType.Line) {
      const styleObj = this.stylesSvc.flowline(json).mxCellCss({asObj: true}) as MxStyleObject;
      const {strokeColor, strokeWidth, dashed, dashPattern, endArrow, startArrow} = styleObj;

      return {
        startArrow,
        endArrow,
        borderColor: strokeColor,
        borderWidth: `${strokeWidth}px`,
        borderStyle: dashed === '0' ? 'solid' : dashPattern === 'none' ? 'dashed' : 'dotted'
      };
    }
  }

  private collectHeaderData(json): IHeaderData {
    const {headerData, name, description} = json;

    return {
      ...(headerData || {legendItems: []}),
      title: name,
      description
    };
  }

  private getMxKey() {
    let mxKey = 0;
    const legendItems = this.diagramJsonSvc.get()?.headerData?.legendItems || [];

    legendItems.forEach(item => {
      if (item.mxKey > mxKey) {
        mxKey = item.mxKey;
      }
    });

    return mxKey + 1;
  }

  private getChangeLogoData(
    fileChangeEvent: Event,
    isUpload: boolean
  ): Partial<ChangeImageDialogData> {
    const title = isUpload
      ? 'CORE.ATTACHMENTS.LEGEND_LOGO_UPLOAD'
      : 'CORE.ATTACHMENTS.LEGEND_LOGO_CHANGE';
    const actionTitle = isUpload
      ? 'CORE.ATTACHMENTS.UPLOAD_LOGO_LABEL'
      : 'CORE.ATTACHMENTS.CHANGE_LOGO';

    return {
      color: 'primary',
      title,
      actionTitle,
      actionType: 'architectureLogo',
      maintainAspectRatio: false,
      fileChangeEvent
    };
  }
}
